"use client";

import { termsData } from "@/data/main-data";
import { useLocale } from "next-intl";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import {
  FaShieldAlt,
  FaFingerprint,
  FaGavel,
  FaUserShield,
  FaRegHandshake,
  FaArrowRight,
  FaArrowLeft,
} from "react-icons/fa";

const icons = [FaFingerprint, FaGavel, FaUserShield, FaRegHandshake];

export default function Terms() {
  const locale = useLocale();
  const isArabic = locale === "ar";
  const [activeIndex, setActiveIndex] = useState(0);
  const terms = isArabic ? termsData.ar : termsData.en;

  return (
    <section className="relative min-h-screen py-24 md:py-40 bg-[#030303] overflow-hidden flex flex-col items-center">
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_20%_30%,rgba(200,169,110,0.05),transparent_50%)]" />
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(circle_at_80%_70%,rgba(200,169,110,0.05),transparent_50%)]" />
      </div>

      <div
        className={`relative z-10 container mx-auto px-6 lg:w-[90%] ${isArabic ? "font-cairo rtl" : "font-sans ltr"}`}
      >
        {/* --- Header --- */}
        <div className="mb-20">
          <motion.div
            initial={{ opacity: 0, x: isArabic ? 50 : -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            className="flex items-center gap-4 mb-4"
          >
            <div className="p-3 bg-[#C8A96E] rounded-lg text-black">
              <FaShieldAlt size={20} />
            </div>
            <h2 className="text-3xl md:text-5xl font-black text-[#EDE5D6]">
              {isArabic ? "الشروط والاحكام" : "Terms & Conditions"}
            </h2>
          </motion.div>
          <p className="text-[#EDE5D6]/40 max-w-xl text-lg font-light">
            {isArabic
              ? "الإطار القانوني المنظم للتعاملات بين المطورين والجهات التمويلية."
              : "The legal framework governing transactions between developers and financing entities."}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 min-h-150">
          
          <div className="w-full lg:w-1/3 flex flex-col gap-3 sticky top-24 self-start">
            {terms.map((section, idx) => {
              const Icon = icons[idx % icons.length];
              const isActive = activeIndex === idx;
              return (
                <button
                  key={idx}
                  onClick={() => setActiveIndex(idx)}
                  className={`relative flex items-center gap-5 p-6 rounded-2xl transition-all duration-500 group outline-none overflow-hidden ${
                    isActive
                      ? "bg-[#C8A96E] text-black shadow-[0_10px_30px_rgba(200,169,110,0.2)] scale-105 z-10"
                      : "bg-white/3 border border-white/5 hover:bg-white/[0.07]"
                  }`}
                >
                  <div
                    className={`p-3 rounded-xl transition-colors ${isActive ? "bg-black/10" : "bg-white/5 text-[#C8A96E]"}`}
                  >
                    <Icon size={22} />
                  </div>

                  <div className="text-start flex-1">
                    <span
                      className={`text-[10px] uppercase font-bold tracking-widest block mb-1 ${isActive ? "text-black/60" : "text-[#C8A96E]/60"}`}
                    >
                      {isArabic ? `المادة 0${idx + 1}` : `Article 0${idx + 1}`}
                    </span>
                    <h3 className="text-lg font-bold leading-tight">
                      {section.title}
                    </h3>
                  </div>

                  <div
                    className={`transition-all duration-500 ${isActive ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"}`}
                  >
                    {isArabic ? <FaArrowLeft /> : <FaArrowRight />}
                  </div>

                  {!isActive && (
                    <div className="absolute inset-0 bg-linear-to-r from-[#C8A96E]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex-1 relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, x: isArabic ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: isArabic ? 20 : -20 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="h-full bg-[#11141D] border border-white/5 rounded-[2.5rem] p-8 md:p-16 relative overflow-hidden shadow-2xl"
              >
                <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#C8A96E]/5 blur-[100px] rounded-full pointer-events-none" />

                <div className="relative z-10">
                  <div className="flex items-center gap-4 mb-10">
                    <div className="h-px flex-1 bg-linear-to-r from-transparent to-[#C8A96E]/30" />
                    <span className="text-[#C8A96E] font-bold text-xs tracking-[0.5em] uppercase">
                      {isArabic ? "تفاصيل البند" : "Clause Details"}
                    </span>
                    <div className="h-px flex-1 bg-linear-to-l from-transparent to-[#C8A96E]/30" />
                  </div>

                  <h3 className="text-3xl md:text-5xl font-black text-white mb-12">
                    {terms[activeIndex].title}
                  </h3>

                  <div className="grid gap-8">
                    {terms[activeIndex].content.map((item, i) => (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.1 }}
                        key={i}
                        className="flex gap-6 group/item"
                      >
                        <div className="mt-2 w-2 h-2 rounded-full bg-[#C8A96E] shadow-[0_0_15px_#C8A96E] shrink-0 group-hover/item:scale-150 transition-transform" />
                        <p className="text-[#EDE5D6]/60 text-lg leading-[1.8] font-light group-hover/item:text-[#EDE5D6] transition-colors">
                          {item}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <span className="absolute -bottom-10 -right-10 text-[20rem] font-black text-white/3 select-none pointer-events-none">
                  0{activeIndex + 1}
                </span>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
